import React, { useEffect, useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { formatDistanceToNowStrict } from "date-fns";
import { Navigate, useMatch, useNavigate, useParams } from "react-router";
import {
  Button,
  Link,
  LongTextLabel,
  NavigationSidebar,
  TreeNode,
  TreeView,
  useSidebarCollapsed,
} from "@sscs/ui";
import { SearchIcon } from "@sscs/ui/icons";
import { getClassName, prettifyLargeCount } from "@sscs/utils";
import { Dialog } from "Components";
import { INSIGHTS_SEARCH_ROUTE, STORE_INSIGHTS_SEARCH_ID } from "Constants";
import { useAppDispatch, useAppSelector, useDialog } from "Hooks";
import { selectUnsavedChangesForPage } from "Modules/notification";
import { useGetWhoamiQuery } from "Services";
import { QUICK_SEARCH_CATEGORY_LABEL } from "../../constants";
import useSearch from "../../hooks/useSearch";
import {
  useGetMySearchesQuery,
  useGetQuickSearchesQuery,
} from "../../services/insights.api";
import { clearGroupFilters } from "../../store";
import { MyInsightsSearch, QuickSearchCategory } from "../../types";
import { getCategorizedSearches } from "../../utils";
import Label from "./Label";
import styles from "./navigation.module.scss";
import SearchMenu from "./SearchMenu";

const LastUpdatedTitle = ({
  lastUpdated,
}: {
  lastUpdated: string | null;
}): React.JSX.Element => (
  <>
    {lastUpdated
      ? `Last updated: ${formatDistanceToNowStrict(new Date(lastUpdated))} ago`
      : "Not counted yet"}
  </>
);

type ActionsProps = {
  size: "small" | "large";
  onSearch: () => void;
};

const Actions = ({ size, onSearch }: ActionsProps): React.JSX.Element => {
  const { groupId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDialog = useDialog();
  const { isDirty } = useSearch();

  return (
    <>
      <div
        className={getClassName(styles.actions, {
          [styles["actions-small"]]: size === "small",
        })}
      >
        <Link
          to={INSIGHTS_SEARCH_ROUTE}
          onClick={(event) => {
            onSearch();

            if (isDirty) {
              event.preventDefault();
              confirmDialog.open();
            } else {
              dispatch(clearGroupFilters(groupId));
            }
          }}
        >
          <Button isFullWidth>
            {size === "small" ? <SearchIcon /> : "Start new search"}
          </Button>
        </Link>
      </div>
      <Dialog
        title="Start new search"
        confirmText="Start new search"
        cancelText="No"
        onConfirm={() => {
          dispatch(clearGroupFilters(groupId));
          navigate(INSIGHTS_SEARCH_ROUTE);
          confirmDialog.close();
        }}
        onCancel={confirmDialog.close}
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.close}
        cancelButtonColor="primary"
        confirmButtonColor="secondary"
      >
        <span>
          Are you sure you want to start new search? Unsaved changes will be
          lost.
        </span>
      </Dialog>
    </>
  );
};

const MySearches = (): React.JSX.Element => {
  const { groupId, searchId } = useParams();
  const { data: whoami } = useGetWhoamiQuery();
  const { data } = useGetMySearchesQuery(groupId ? { groupId } : skipToken);

  const [anySearches, groupSearches] = useMemo(() => {
    const anySearches: MyInsightsSearch[] = [];
    const groupSearches: Record<string, MyInsightsSearch[]> = {};

    data?.forEach((search) => {
      if (search.group === null) {
        anySearches.push(search);
      } else {
        if (!groupSearches[search.group]) {
          groupSearches[search.group] = [];
        }

        groupSearches[search.group].push(search);
      }
    });

    return [anySearches, groupSearches];
  }, [data]);

  const nodes = useMemo<TreeNode[]>(() => {
    const result: TreeNode[] = [];

    if (anySearches.length > 0) {
      result.push({
        id: "any-group",
        label: "Use in any group",
        expandableOnly: true,
        children: anySearches.map((search) => ({
          id: search.id,
          label: <Label search={search} />,
          count: prettifyLargeCount(search.count),
          tooltip: <LastUpdatedTitle lastUpdated={search.last_updated} />,
          menu: <SearchMenu search={search} />,
        })),
      });
    }

    Object.keys(groupSearches).forEach((searchesGroupId) => {
      result.push({
        id: searchesGroupId,
        expandableOnly: true,
        label: (
          <LongTextLabel
            label={`${searchesGroupId === groupId ? "Current group" : "Group"}: ${
              whoami?.org_groups.find(
                (whoamiGroup) => whoamiGroup.group_id === searchesGroupId,
              )?.name ?? ""
            }`}
            className={styles["group-name"]}
          />
        ),
        children: groupSearches[searchesGroupId].map((search) => ({
          id: search.id,
          label: <Label search={search} />,
          count: prettifyLargeCount(search.count),
          tooltip: <LastUpdatedTitle lastUpdated={search.last_updated} />,
          menu: <SearchMenu search={search} />,
        })),
      });
    });

    return result;
  }, [anySearches, groupSearches, groupId, whoami]);

  return (
    <div className={styles["my-searches"]}>
      <TreeView
        selected={searchId ?? null}
        title="My searches"
        nodes={nodes}
        empty={{ subject: "saved searches" }}
      />
    </div>
  );
};

const QuickSearches = (): React.JSX.Element => {
  const { groupId, searchId } = useParams();
  const { data } = useGetQuickSearchesQuery(groupId ? { groupId } : skipToken);
  const categorizedSearches = useMemo(
    () => getCategorizedSearches(data),
    [data],
  );

  const nodes = useMemo<TreeNode[]>(
    () =>
      Object.keys(categorizedSearches)
        .map((category: QuickSearchCategory): TreeNode | null => {
          if (categorizedSearches[category].length === 0) return null;

          return {
            id: category,
            label: QUICK_SEARCH_CATEGORY_LABEL[category],
            expandableOnly: true,
            children: categorizedSearches[category].map((search) => ({
              id: search.id,
              label: <Label search={search} />,
              count: prettifyLargeCount(search.count),
              tooltip: <LastUpdatedTitle lastUpdated={search.last_updated} />,
            })),
          };
        })
        .filter((node): node is TreeNode => node !== null),
    [categorizedSearches],
  );

  return (
    <div className={styles["quick-searches"]}>
      <TreeView
        selected={searchId ?? null}
        title="Quick searches"
        nodes={nodes}
        empty={{ subject: "quick searches" }}
      />
    </div>
  );
};

const Navigation = (): React.JSX.Element => {
  const { groupId, searchId } = useParams();
  const [isCollapsed, setIsCollapsed] = useSidebarCollapsed("insights");
  const unsavedChanges = useAppSelector((state) =>
    selectUnsavedChangesForPage(state, "insights"),
  );
  // Matched loosely: a navigation within the unsaved search's nested routes is still on
  // that search, and matching exactly reads it as having left, redirecting mid-flight.
  const onUnsavedChangeRoute = Boolean(
    useMatch({
      path: groupId ? (unsavedChanges[groupId]?.navigateTo ?? "*") : "*",
      end: false,
    }),
  );

  useEffect(() => {
    if (searchId) {
      localStorage.setItem(STORE_INSIGHTS_SEARCH_ID, searchId);
    }
  }, [searchId]);

  // First show unsaved changes
  if (groupId && unsavedChanges[groupId] && !onUnsavedChangeRoute) {
    return <Navigate to={unsavedChanges[groupId].navigateTo} replace />;
  }

  return (
    <NavigationSidebar
      aria-label="insights-navigation"
      storageKey="insights"
      title="Insights"
      isCollapsed={isCollapsed}
      onCollapse={setIsCollapsed}
    >
      <div className={styles["sidebar-inner"]}>
        <Actions
          size={isCollapsed ? "small" : "large"}
          onSearch={() => {
            if (isCollapsed) {
              setIsCollapsed(false);
            }
          }}
        />
        <MySearches />
        <QuickSearches />
      </div>
    </NavigationSidebar>
  );
};

export default Navigation;
